import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  Button,
  GeneralList,
  GeneralListSortableTh,
  PrinterIcon,
  RefreshIcon,
  SearchIcon,
  Select,
  UserIcon,
  VesselSelect,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { CrewReportViewAction, useCrewReportOpen, WITHIN_DAYS_OPTIONS } from './reportShared';
import { coeWatchlistPrintPath } from './reports/coeWatchlistPrintMeta';
import type { CoeWatchlistResponse, CoeWatchlistRow } from './types';

const ROUTE = '/crew/coe-watchlist';
const STORAGE_KEY = getListStateStorageKey(ROUTE);

type UrgencyTab = 'all' | 'coeSoon' | 'overdue';

type Filters = {
  tab: UrgencyTab;
  vesselId: string;
  withinDays: string;
  search: string;
};

const display = (value: string | number | null | undefined) =>
  value == null || value === '' ? '—' : String(value);

const displayDate = (value: string | null | undefined) => (value ? formatDate(value) : '—');

function sortValue(row: CoeWatchlistRow, key: string): string | number {
  switch (key) {
    case 'name':
      return (row.name || '').toLowerCase();
    case 'vessel':
      return (row.vessel || '').toLowerCase();
    case 'rank':
      return (row.rank || '').toLowerCase();
    case 'signOnDate':
      return row.signOnDate || '';
    case 'monthsOnboard':
      return row.monthsOnboard ?? -1;
    case 'coeDate':
      return row.coeDate || '';
    case 'daysToCoe':
      return row.daysToCoe ?? Number.POSITIVE_INFINITY;
    case 'intentionSignOffDate':
      return row.intentionSignOffDate || '';
    case 'extCoeDate':
      return row.extCoeDate || '';
    default:
      return row.transactionId;
  }
}

const CoeWatchlistPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();

  const [persisted, setPersisted] = usePersistedListFilters<Filters>(STORAGE_KEY);
  const [tab, setTab] = useState<UrgencyTab>(persisted?.tab ?? 'all');
  const [vesselId, setVesselId] = useState(persisted?.vesselId ?? '');
  const [withinDays, setWithinDays] = useState(persisted?.withinDays ?? '90');
  const [searchInput, setSearchInput] = useState(persisted?.search ?? '');
  const [search, setSearch] = useState(persisted?.search ?? '');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort({ key: 'daysToCoe', direction: 'asc' });

  useEffect(() => {
    setPersisted({ tab, vesselId, withinDays, search });
  }, [tab, vesselId, withinDays, search, setPersisted]);

  useEffect(() => {
    setPage(1);
  }, [tab, vesselId, withinDays, search, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('withinDays', withinDays || '90');
    if (vesselId) params.set('vesselId', vesselId);
    return `/crew/reports/coe-watchlist?${params.toString()}`;
  }, [vesselId, withinDays]);

  const { data, error, isLoading, mutate } = useSWR<CoeWatchlistResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const handleSearch = useCallback(() => {
    setSearch(searchInput.trim());
    setPage(1);
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    let list = data?.rows ?? [];
    if (tab === 'overdue') list = list.filter((r) => r.intentionOverdue);
    if (tab === 'coeSoon') {
      list = list.filter((r) => r.daysToCoe != null && r.daysToCoe >= 0);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.vessel && r.vessel.toLowerCase().includes(q)) ||
          (r.rank && r.rank.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [data?.rows, tab, search]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return (a.transactionId - b.transactionId) * dir;
    });
    return rows;
  }, [filteredRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(search.trim() || vesselId || withinDays !== '90' || tab !== 'all');

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setTab('all');
    setVesselId('');
    setWithinDays('90');
  };

  return (
    <GeneralList
      tabs={[
        { id: 'all', label: 'All' },
        { id: 'coeSoon', label: 'COE in window' },
        { id: 'overdue', label: 'Overdue intentions' },
      ]}
      activeTab={tab}
      onTabChange={(value) => setTab(value as UrgencyTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Name, vessel, rank…',
        title: 'Searches crew name, vessel, and rank',
        onSubmit: handleSearch,
      }}
      filtersInline
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[14rem] max-w-full">
            <VesselSelect
              value={vesselId}
              placeholder="Vessel"
              includeIds={vesselId ? [vesselId] : undefined}
              onChange={(e) => setVesselId(String(e.target.value))}
              triggerClassName="!h-10 !min-h-10 !py-0 box-border"
            />
          </div>
          <div className="min-w-[120px] flex-1 basis-[120px] sm:max-w-[160px]">
            <Select
              value={withinDays}
              options={WITHIN_DAYS_OPTIONS}
              placeholder="Within days"
              showClearButton={false}
              onChange={(e) => setWithinDays(String(e.target.value))}
              triggerClassName="!h-10 !min-h-10 !py-0 box-border"
            />
          </div>
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      actions={
        <div className="flex h-10 items-center gap-2">
          <Button type="button" className="h-10" disabled={isLoading} onClick={handleSearch}>
            <SearchIcon className="h-4 w-4" />
            Search
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10"
            disabled={isLoading}
            onClick={() => void mutate()}
            title="Refresh"
          >
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </Button>
          <Link
            to={coeWatchlistPrintPath({ tab, vesselId, withinDays, search })}
            title="Print preview"
            className={cn(
              'inline-flex h-10 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors',
              colors.border,
              colors.text,
              'hover:bg-black/5 dark:hover:bg-white/10',
            )}
          >
            <PrinterIcon className="h-4 w-4" />
            Print
          </Link>
        </div>
      }
      isLoading={isLoading}
      loadingLabel="Loading COE watchlist…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon: <UserIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching watch items' : 'No COE / intention items in this window',
        hint: hasFilters
          ? 'Try adjusting filters or clearing the search.'
          : 'Crew approaching COE will show up here.',
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : undefined,
      }}
      banner={
        error ? (
          <p className={cn('rounded-xl border px-3 py-2 text-sm', colors.error)}>
            Could not load watchlist.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full min-w-[1100px] text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-12 font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap">
              Crew
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="whitespace-nowrap">
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="rank" className="whitespace-nowrap">
              Rank
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="signOnDate" className="whitespace-nowrap">
              S/On
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="monthsOnboard" className="whitespace-nowrap" align="right">
              M.ONB
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="coeDate" className="whitespace-nowrap">
              COE
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="daysToCoe" className="whitespace-nowrap" align="right">
              Days to COE
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="intentionSignOffDate" className="whitespace-nowrap">
              Intention S/Off
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="extCoeDate" className="whitespace-nowrap">
              Ext. COE
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => (
            <tr
              key={row.transactionId}
              onClick={() => openCrew(row.crewId)}
              className={cn(
                'group cursor-pointer border-b transition last:border-b-0',
                colors.border,
                colors.interactiveHover,
              )}
            >
              <td onClick={(e) => e.stopPropagation()}>
                <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />
              </td>
              <td>
                <Link
                  to={`/crew/${row.crewId}`}
                  state={{ from: listReturnPath }}
                  className={cn('text-sm font-medium hover:underline', colors.primaryFg)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.name}
                </Link>
              </td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.vessel)}</td>
              <td className={cn('whitespace-nowrap', colors.text)}>{display(row.rank)}</td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.signOnDate)}</td>
              <td className={cn('whitespace-nowrap text-right tabular-nums', colors.text)}>
                {display(row.monthsOnboard)}
              </td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.coeDate)}</td>
              <td className="text-right">
                {row.daysToCoe == null ? (
                  <span className={colors.textMuted}>—</span>
                ) : (
                  <Badge
                    variant={row.daysToCoe < 0 ? 'danger' : 'warning'}
                    className="rounded-md px-2 py-0.5"
                  >
                    {row.daysToCoe}
                  </Badge>
                )}
              </td>
              <td>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                  <span className={colors.textMuted}>{displayDate(row.intentionSignOffDate)}</span>
                  {row.intentionOverdue ? (
                    <Badge variant="danger" className="rounded-md px-1.5 py-0.5 text-xs">
                      Overdue
                    </Badge>
                  ) : null}
                </span>
              </td>
              <td className={cn('whitespace-nowrap', colors.textMuted)}>{displayDate(row.extCoeDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default CoeWatchlistPage;
